import Grid from '@mui/material/Grid'
import clsx from 'clsx'
import type { ReactElement, ReactNode } from 'react'
import { Children, cloneElement, isValidElement } from 'react'
import { Labeled } from 'react-admin'
import { RaGrid } from './RaGrid'

/**
 * A show layout whose children may be arbitrarily nested in <RaGrid>.
 *
 * <SimpleShowLayout> wraps each of its direct children in <Labeled>, which
 * means a field nested inside a grid cell never gets its label. GridShowLayout
 * walks through Grid elements instead of stopping at them, so the layout can be
 * a grid and the leaves still get labelled:
 *
 *   <GridShowLayout>
 *     <RaGrid container>
 *       <RaGrid item md={4}><TextField source="name" /></RaGrid>
 *     </RaGrid>
 *   </GridShowLayout>
 *
 * The `ra-field` / `ra-field-<source>` class names match what
 * <SimpleShowLayout> emits, so field-targeted CSS keeps working.
 */
const renderChildren = (children: ReactNode): ReactNode =>
  Children.map(children, (child) => {
    if (!isValidElement(child)) return child

    const props = child.props as { children?: ReactNode; label?: ReactNode; source?: string }

    // Grid elements are structure, not content: recurse past them.
    if (child.type === RaGrid || child.type === Grid) {
      return cloneElement(child as ReactElement<any>, {}, renderChildren(props.children))
    }

    // `label={false}` is react-admin's opt-out and <Labeled> honours it.
    return (
      <Labeled
        label={props.label}
        source={props.source}
        className={clsx('ra-field', props.source && `ra-field-${props.source}`)}
      >
        {child as ReactElement<any>}
      </Labeled>
    )
  })

export interface GridShowLayoutProps {
  children?: ReactNode
  className?: string
}

export const GridShowLayout = ({ children, className }: GridShowLayoutProps) => (
  <div className={className}>{renderChildren(children)}</div>
)

export default GridShowLayout
